import {
  memo,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ArrayField, useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { OnChangeValue } from 'react-select';
import { toast } from 'react-toastify';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import AddIcon from '@material-ui/icons/Add';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { DateRange } from '@mui/icons-material';
import moment from 'moment';
import { nanoid } from 'nanoid';
import { timesheetsUrl, workTimeFullReportUrl } from 'router/url';
import { ContainedButton } from 'components/Button';
import FilesUploadDialog from 'components/Dialog/FilesUploadDialog/FilesUploadDialog';
import ProjectDateRangeDialog from 'components/Dialog/ProjectDateRangeDialog';
import DownloadFileButton from 'components/DownloadFileButton';
import ReactSelect from 'components/ReactSelect/ReactSelect';
import ToolTipCell from 'components/ToolTipCell';
import { ContractTypes } from 'modules/BusinessTrips/Dialog/types';
import { useReportingMode } from 'modules/OrderReporting/useReportingMode';
import {
  getReportingErrorMessage,
  recordsDaysWithoutWork,
} from 'modules/OrderReporting/utils';
import { ClientData, TimesheetData } from 'modules/WorkTime/types';
import { AssignButton, GridWrapper } from 'modules/WorkTime/WorkTime.css';
import {
  mapISODurationToString,
  mapStringToDuration,
} from 'utils/durationFunctions';
import { deleteFetch, postFetch, putFetch } from 'utils/fetchFunctions';
import { useToggleState } from 'utils/hooks/useToggleState';
import { isTimeOffProjectCategory } from 'utils/projectCategories';
import { isHoursOnlyReporting, parseWorkingHours } from 'utils/reportingMode';
import { RoleTypes } from 'utils/rolesTypes';
import { UserContext } from '../../globalContext/UserContext';
import Datepicker from '../Datepicker';
import AssignProjectDialog from '../Dialog/AssignProjectDialog';
import { getFullReportFooterSpans, getFullReportHeadData } from './consts';
import { DaysWithoutWorkCounterCell } from './daysWithoutWork';
import EmptyRow from './EmptyRow';
import FullReportRow from './FullReportRow';
import FullReportSaveControls from './FullReportSaveControls';
import { OrderCellContent } from './OrderCell';
import {
  clearOrderAllocation,
  getCorrectionEntries,
  getOrderScopeByDate,
  hasNonStandardMultiplier,
} from './orderPresentation';
import { ServiceBreakCounterCell } from './serviceBreakCounters';
import { sortDayEntries } from './sortDayEntries';
import {
  discardsWorkEdits,
  getFormattedFullReportData,
  getFullReportDayTimes,
  getInvalidWorktime,
  getInvalidWorktimeTitle,
  getNewEntryProjects,
  getWorkTimeDuration,
  getWorkTimeMidnightDuration,
  isClearedExtraEntry,
  isDayWithoutWorkRow,
  keepsReportedWork,
  projectsFilter,
  shouldUseDefaultClientAndProject,
} from './tableUtils';
import {
  DayProjectOptionValue,
  FetchedFile,
  FullReport,
  FullReportDay,
  InvalidWorktimeProps,
  ProjectBasicInfo,
  SubmitResponseData,
  TableFullReportProps as TableProps,
} from './types';
import {
  CustomProjectsContainer,
  FullReportTableContainer,
  GreyBorder,
  Paper,
  Table as TableComponent,
  TableBody,
  TableCell,
  TableCellCustom,
  TableFooter,
  TableFooterCell,
  TableFooterRow,
  TableHeadCell,
  TableHeader,
  TableRow,
} from './Table.css';

type FullReportDayLike = Partial<FullReportDay> & Record<string, any>;

const FullReportTable = ({
  headCustoms,
  projectsData,
  clientsData,
  fetchClientsAndProjects,
  timesheetData,
  date,
  setDate,
  setTimesheetUsername,
  selectedUser,
  setSelectedUser,
  setIsFormedChanged,
  myTimesheetLoaded,
  isUserAuthorized,
  loading,
  reportAvailable,
  defaultProject,
  rowRef,
  defaultClient,
  sheetId,
  fullReportData,
  projectsOptions,
  setProjectsOptions,
  fetchFullReport,
  userContract,
  viewedUsername,
}: TableProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const [addChecked, handleAddChecked] = useToggleState(false);
  const [openFileUploadArea, setOpenFileUploadArea] = useToggleState(false);
  const [uploadedDayId, setUploadedDayId] = useState('-1');
  const [uploadedDayFiles, setUploadedDayFiles] = useState<FetchedFile[]>();
  const [isFileDeleted, setFileDeleted] = useState(false);
  const [areFilesSend, setAreFilesSend] = useState(false);
  const [isDateRangeDialogOpen, setIsDateRangeDialogOpen] =
    useToggleState(false);
  const [loadingToDisableButton, setLoadingToDisableButton] = useState(false);
  const [invalidWorktime, setInvalidWorktime] = useState<
    InvalidWorktimeProps[]
  >([]);
  const [pendingOrderDayIds, setPendingOrderDayIds] = useState<Set<string>>(
    new Set(),
  );
  const { username, roles } = useContext(UserContext);
  // Assignment availability follows the owner of the viewed timesheet.
  const timesheetOwner = viewedUsername || username;

  const userData = useMemo(
    () => timesheetData?.find((data) => data?.username === username),
    [timesheetData, username],
  );

  // Labels and counters follow the viewed account when the report says
  // which contract it has.
  const viewedContract = fullReportData?.contractType || userContract;
  const isEmploymentContract =
    viewedContract === ContractTypes.EMPLOYMENT_CONTRACT;
  const hoursOnly = isHoursOnlyReporting(
    fullReportData?.hoursOnly,
    viewedContract,
  );
  const daysWithoutWorkRecorded = recordsDaysWithoutWork(viewedContract);
  const { reportingMode } = useReportingMode();
  // Assignment scope per date from the backend (change request of 26.10,
  // P2 and amendment A1): days of an employment contract on that date are
  // out of scope and work as with reporting OFF. The column shows while
  // some day of the month is in scope; older backends send no scope and the
  // module mode alone decides.
  const orderScopeByDate = useMemo(
    () => getOrderScopeByDate(fullReportData?.days),
    [fullReportData?.days],
  );
  const showOrder =
    reportingMode?.mode !== 'OFF' &&
    (!orderScopeByDate.size ||
      Array.from(orderScopeByDate.values()).some(Boolean));
  const showMultiplier = useMemo(
    () => hasNonStandardMultiplier(fullReportData?.days, projectsData),
    [fullReportData?.days, projectsData],
  );
  const fullReportHeadData = useMemo(
    () => getFullReportHeadData(hoursOnly, { showOrder, showMultiplier }),
    [hoursOnly, showMultiplier, showOrder],
  );
  const customHeadData = useMemo(
    () => (headCustoms || []).filter(({ key }) => showOrder || key !== 'order'),
    [headCustoms, showOrder],
  );
  const corrections = useMemo(
    () =>
      getCorrectionEntries(
        fullReportData?.days || [],
        fullReportData?.customDailyTimes || [],
      ),
    [fullReportData?.customDailyTimes, fullReportData?.days],
  );
  // Clients with absence projects stay selectable when orders are enforced.
  const exemptClientIds = useMemo(
    () =>
      new Set(
        (projectsData || [])
          .filter(({ projectCategory }) =>
            isTimeOffProjectCategory(projectCategory),
          )
          .map(({ client }) => client?.id)
          .filter((id): id is number => typeof id === 'number'),
      ),
    [projectsData],
  );

  const isAccountingOrSales = useMemo(() => {
    return (
      roles.includes(RoleTypes.RoleAccounting) ||
      roles.includes(RoleTypes.RoleSales) ||
      roles.includes(RoleTypes.RoleDEVHR)
    );
  }, [roles]);

  const upLg = useMediaQuery(theme.breakpoints.up('lg'));

  const downloadExcelUrl = `${timesheetsUrl}/${
    selectedUser?.username ? selectedUser.username : username
  }/${workTimeFullReportUrl}/export?yearMonth=${moment(date).format(
    'YYYY-MM',
  )}&exportFormat=XLSX`;

  const { control, formState, reset, getValues, register, setValue } =
    useForm<FullReport>({
      defaultValues: fullReportData,
    });

  const { fields, insert, remove } = useFieldArray({
    control,
    name: 'days',
  });

  const sortedFields = useMemo(() => sortDayEntries(fields), [fields]);

  const savedDays = useMemo(
    () => new Map((fullReportData?.days || []).map((day) => [day.dayId, day])),
    [fullReportData?.days],
  );
  const getSavedDay = useCallback(
    (dayId?: string) => (dayId ? savedDays.get(dayId) : undefined),
    [savedDays],
  );
  const savedDayReportsWork = useCallback(
    (dayId?: string) => {
      const savedDay = getSavedDay(dayId);
      return savedDay ? keepsReportedWork(savedDay, hoursOnly) : false;
    },
    [getSavedDay, hoursOnly],
  );

  const handleProjectChange = useCallback(
    (dayId: string, index: number) =>
      async (e: OnChangeValue<DayProjectOptionValue, boolean>) => {
        const newDays = getValues()?.days.map((el) =>
          el.dayId === dayId
            ? { ...el, projectBasicInfo: e as unknown as ProjectBasicInfo }
            : el,
        );
        setPendingOrderDayIds((current) => new Set(current).add(dayId));
        const correctDay = newDays?.find((day) => day.dayId === dayId);
        if (newDays && correctDay?.projectBasicInfo) {
          setValue(
            `days[${index}].multiplicator`,
            correctDay?.projectBasicInfo.multiplier,
          );
        } else {
          setValue(`days[${index}].multiplicator`, '');
        }
      },
    [setValue, getValues],
  );

  const clearProjectField = useCallback(
    (dayId: string, index: number) => {
      handleProjectChange(dayId, index)(null);
      setValue(`days[${index}].project`, '');
      setValue(`days[${index}].multiplicator`, '');
    },
    [handleProjectChange, setValue],
  );

  const handleClientChange = useCallback(
    (dayId: string, index: number) => (_e: ClientData) => {
      clearProjectField(dayId, index);
      const projectsForClient = projectsFilter(
        projectsData,
        getValues().days?.find((day) => day.dayId === dayId)?.client?.name,
      ).map((project) => ({
        value: project.value,
        name: project.name,
        status: project.status,
        reportingCustomData: project.reportingCustomData,
        projectCategory: project.projectCategory,
        multiplier: project.multiplier,
        tooltipMessage: project?.tooltipMessage,
      }));
      setProjectsOptions((prevProjectsOptions) =>
        prevProjectsOptions?.map((p) =>
          p.dayId === dayId ? { ...p, projects: projectsForClient } : p,
        ),
      );
    },
    [clearProjectField, getValues, setProjectsOptions, projectsData],
  );

  const isUserSettled = !!fullReportData?.settled;
  const isTimesheetDisabled = !myTimesheetLoaded || isUserSettled;
  const footerSpans = getFullReportFooterSpans(
    fullReportHeadData.length,
    isUserSettled,
    isEmploymentContract ? 3 : 2,
  );

  const isLeaveDay = useCallback(
    (dayId: string) => {
      const days = getValues()?.days;
      if (days) {
        const selectOption = days.find((el) => el?.dayId === dayId)?.project
          ?.projectCategory;
        return isTimeOffProjectCategory(selectOption);
      } else if (fullReportData) {
        const selectOption = fullReportData?.days.find(
          (el) => el?.dayId === dayId,
        )?.projectBasicInfo?.projectCategory;

        return isTimeOffProjectCategory(selectOption);
      }
      return false;
    },
    [getValues, fullReportData],
  );

  const checkIfDayIsFree = useCallback(
    (dayId: string, entry: FullReportDayLike) => {
      return (
        isLeaveDay(dayId) ||
        entry.dayOfWeek === 'SATURDAY' ||
        entry.dayOfWeek === 'SUNDAY' ||
        entry.holiday ||
        entry.bankHoliday
      );
    },
    [isLeaveDay],
  );

  const getDefaultValueForProjectOrClient = useCallback(
    (input: 'project' | 'client') => {
      switch (input) {
        case 'project':
          return defaultProject.id
            ? {
                client: {
                  id: defaultClient.id,
                  clientName: defaultClient.name,
                  status: 'ACTIVE',
                },
                contactPerson: '',
                multiplicator: defaultProject.multiplicatorValue,
                multiplier: defaultProject.multiplicator,
                name: defaultProject.name,
                projectCategory: defaultProject.projectCategory,
                projectName: defaultProject.name,
                reportingCustomData: false,
                status: 'ACTIVE',
                value: defaultProject.id,
              }
            : undefined;
        case 'client':
          return defaultClient.id
            ? {
                accountingNotes: undefined,
                clientName: defaultClient.name,
                name: defaultClient.name,
                status: 'ACTIVE',
                value: defaultClient.id,
              }
            : undefined;
      }
    },
    [defaultClient, defaultProject],
  );

  const getProjectsOptions = useCallback(
    (dayId?: string) => {
      return projectsOptions?.find((p) => p.dayId === dayId)?.projects;
    },
    [projectsOptions],
  );

  const checkWhatToRender = useCallback(
    (entry: FullReportDayLike, input: 'project' | 'client') => {
      const value = entry[input];
      if (value) {
        if (input === 'client') {
          const result = clientsData?.find(
            (client) => value.id === client.value,
          );

          return result
            ? {
                accountingNotes: undefined,
                clientName: result?.clientName,
                name: result?.clientName,
                status: result?.status,
                value: result?.value,
              }
            : value;
        } else {
          const result = projectsData?.find(
            (project) => value.id === project.value,
          );
          return result
            ? {
                client: {
                  id: result?.client.id,
                  clientName: result?.client.clientName,
                  status: result?.client.status,
                },
                contactPerson: '',
                multiplicator: result?.multiplicator,
                multiplier: result?.multiplier,
                name: value.name,
                projectCategory: result?.projectCategory,
                projectName: result?.projectName,
                reportingCustomData: false,
                status: result?.status,
                value: value.id,
              }
            : value;
        }
      } else {
        if (
          entry.dayId &&
          shouldUseDefaultClientAndProject(
            entry,
            Boolean(checkIfDayIsFree(entry.dayId, entry)),
            hoursOnly,
          )
        ) {
          return getDefaultValueForProjectOrClient(input);
        } else {
          return value;
        }
      }
    },
    [
      checkIfDayIsFree,
      clientsData,
      getDefaultValueForProjectOrClient,
      hoursOnly,
      projectsData,
    ],
  );

  const parseDays = useCallback(
    (days: FullReportDay[]) => {
      return days.map((entry) => ({
        date: entry.date,
        id: entry.id,
        ...getFullReportDayTimes(entry, hoursOnly),
        multiplicator: 'STANDARD_100',
        client: entry.id
          ? entry.client || checkWhatToRender(entry, 'client')
          : entry.client,
        project: entry.id
          ? entry.project || checkWhatToRender(entry, 'project')
          : entry.project,
        comment: entry.comment || '',
        projectBasicInfo: getValues()
          ?.days.filter((day) => day.dayId === entry.dayId)
          .map((day) => day.projectBasicInfo)[0],
        parentId: entry?.parentId || undefined,
      }));
    },
    [checkWhatToRender, getValues, hoursOnly],
  );

  useEffect(() => {
    if (areFilesSend) {
      setAreFilesSend(false);
      fetchFullReport(reset);
    }
  }, [areFilesSend, fetchFullReport, reset]);

  const handleFileUploadClick = useCallback(
    (entry: Partial<ArrayField<Record<string, any>>>) => () => {
      if (entry.dayId) {
        setUploadedDayId(entry.dayId);
      }
      setUploadedDayFiles(entry?.attachments);
      setOpenFileUploadArea();
    },
    [setOpenFileUploadArea, setUploadedDayFiles],
  );

  const sendFile = useCallback(
    async (file) => {
      const formFile = new FormData();
      formFile.append('file', file);

      await postFetch({
        url: `${timesheetsUrl}/${sheetId}/daily-times/${uploadedDayId}/files`,
        body: formFile,
        contentType: 'formdata',
        intl,
        label: 'ADD_FILES_TO_DAILYTIME',
      });
    },
    [sheetId, uploadedDayId, intl],
  );

  const deleteFile = useCallback(
    async (fileId: number) => {
      const { ok } = await deleteFetch({
        url: `${timesheetsUrl}/${sheetId}/daily-times/${uploadedDayId}/${fileId}`,
        intl,
        label: 'DELETE_FILES_FROM_DAILYTIME',
      });
      if (ok) {
        setFileDeleted(true);
      }
      return ok;
    },
    [intl, sheetId, uploadedDayId, setFileDeleted],
  );

  const handleFileDialogClose = () => {
    setOpenFileUploadArea();
    if (isFileDeleted) {
      setFileDeleted(false);
      fetchFullReport(reset);
    }
  };

  useEffect(() => {
    setPendingOrderDayIds(new Set());
  }, [fullReportData]);

  const addEntryToDailytime = useCallback(
    (entry: Partial<ArrayField<Record<string, any>, 'id'>>, index: number) =>
      async () => {
        const newEntry = {
          ...clearOrderAllocation(entry),
          dayId: nanoid(), // temporary id for dayId dependent logic
          id: null,
          parentId: entry.id,
          startTime: null,
          endTime: null,
          breakTime: null,
          manHours: '',
          hours: '',
          savedMillis: null,
          comment: '',
          client: defaultClient,
          projectBasicInfo: defaultProject,
          project: { name: defaultProject?.name, id: defaultProject?.id },
          multiplicator: { label: 'STANDARD_100', name: 'Standard (100%)' },
        } as unknown as FullReportDay;

        insert(index + 1, newEntry);
        setProjectsOptions((prevState) => [
          ...(prevState ? prevState : []),
          getNewEntryProjects(newEntry, projectsData, defaultClient?.name),
        ]);
      },
    [insert, defaultClient, defaultProject, projectsData, setProjectsOptions],
  );

  const onSubmit = useCallback(async () => {
    if (
      hoursOnly &&
      getValues().days?.some((day) => !parseWorkingHours(day.hours).valid)
    ) {
      toast.error(
        intl.formatMessage({ id: 'FULL_REPORT.WORKING_HOURS_INVALID' }),
      );
      return;
    }
    setLoadingToDisableButton(true);
    // A row whose work edits the save would discard is sent as loaded; a
    // cleared extra entry is left out, so the backend deletes it.
    const days = getValues()
      .days.filter((day) => !isClearedExtraEntry(day, hoursOnly))
      .map((day) => {
        const savedDay = getSavedDay(day.dayId);
        return savedDay && discardsWorkEdits(day, savedDay, hoursOnly)
          ? {
              ...day,
              client: checkWhatToRender(
                savedDay,
                'client',
              ) as FullReportDay['client'],
              project: checkWhatToRender(
                savedDay,
                'project',
              ) as FullReportDay['project'],
            }
          : day;
      });
    const body = {
      month: date!.getMonth() + 1,
      year: date!.getFullYear(),
      days: getFormattedFullReportData(parseDays(days)),
    };

    const { ok, data } = await putFetch<SubmitResponseData>({
      url: `${timesheetsUrl}/${username}/${workTimeFullReportUrl}`,
      intl,
      body,
      label: 'WORK_TIME_TAB.FULL_REPORT.SUBMIT',
      showErrorToast: false,
    });
    if (ok) {
      await fetchFullReport(reset);
      setIsFormedChanged(false);
    } else {
      toast.error(
        getReportingErrorMessage(
          data,
          intl,
          'WORK_TIME_TAB.FULL_REPORT.SUBMIT.TOAST_DENIED',
        ),
      );
    }
    setLoadingToDisableButton(false);
  }, [
    hoursOnly,
    setLoadingToDisableButton,
    date,
    checkWhatToRender,
    getSavedDay,
    parseDays,
    getValues,
    username,
    intl,
    fetchFullReport,
    reset,
    setIsFormedChanged,
  ]);

  const onDateChange = useCallback(
    (date: SetStateAction<Date | null>) => {
      setDate(date);
    },
    [setDate],
  );
  const handleClearButton = useCallback(() => {
    setDate(moment().toDate());
  }, [setDate]);

  const handleSelectUser = useCallback(
    (e: OnChangeValue<TimesheetData, boolean>) => {
      if (e) {
        e = e as TimesheetData;
        setSelectedUser(e);
        if (e.username) {
          setTimesheetUsername(e.username);
        }
      } else {
        setTimesheetUsername('');
        setSelectedUser({ name: '', value: 0, username: '' });
      }
    },
    [setSelectedUser, setTimesheetUsername],
  );

  const getDefaultProject = useCallback(
    (entry: Partial<ArrayField<Record<string, any>, 'id'>>) => {
      const isDayWithoutWorkWithoutProject =
        !entry.project && isDayWithoutWorkRow(hoursOnly, entry.hours);
      if (
        !checkIfDayIsFree(entry.dayId, entry) &&
        !isDayWithoutWorkWithoutProject
      ) {
        return defaultProject.id ? defaultProject : undefined;
      } else {
        return undefined;
      }
    },
    [defaultProject, checkIfDayIsFree, hoursOnly],
  );

  const onInvalidWorktime = useCallback(() => {
    const notEmptyDays = parseDays(getValues().days)?.filter(
      (day) => day.startTime !== '' || day.endTime !== '',
    );
    const invalidWorktimeNotEmptyDays = getInvalidWorktime(notEmptyDays);
    if (
      invalidWorktimeNotEmptyDays?.length ||
      (!invalidWorktimeNotEmptyDays?.length && invalidWorktime?.length)
    ) {
      setInvalidWorktime(invalidWorktimeNotEmptyDays);
    }
  }, [getValues, parseDays, invalidWorktime?.length]);

  const deleteEntryFromDailytime = useCallback(
    (_id: string, index: number) => async () => {
      setValue(`days[${index}].startTime`, '');
      setValue(`days[${index}].endTime`, '');
      onInvalidWorktime();
      remove(index);
    },
    [remove, setValue, onInvalidWorktime],
  );

  const handleRefreshAfterTimeChange = useCallback(
    (index: number) => () => {
      if (getValues().days?.length && getValues().days[index]) {
        const { startTime, endTime, breakTime } = getValues().days[index];
        if (startTime && endTime) {
          if (startTime < endTime) {
            setValue(
              `days[${index}].manHours`,
              mapISODurationToString(
                String(getWorkTimeDuration(startTime, endTime, breakTime)),
              ),
            );
          } else if (mapStringToDuration(endTime).asHours() === 0) {
            setValue(
              `days[${index}].manHours`,
              mapISODurationToString(
                String(getWorkTimeMidnightDuration(startTime, breakTime)),
              ),
            );
          }
        } else {
          setValue(`days[${index}].manHours`, '');
        }
      }
    },
    [getValues, setValue],
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      rowRef?.current?.scrollIntoView?.({
        block: 'center',
        behavior: 'smooth',
      });
    }, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [rowRef]);

  return (
    <>
      <GridWrapper
        container
        spacing={8}
        direction="row"
        alignItems="center"
        justifyContent="center"
      >
        <Grid container item spacing={1} md={12} lg={6}>
          <Grid
            item
            xs={12}
            sm={12}
            md={4}
            lg={5}
            style={{ maxWidth: upLg ? 250 : undefined }}
          >
            <GreyBorder>
              {reportAvailable &&
                `${intl.formatMessage({
                  id: 'SUMMARY.WORK_DAYS',
                })}: ${fullReportData?.workingDaysInMonth}`}
            </GreyBorder>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={5}>
            <Datepicker
              label={intl.formatMessage({
                id: 'TABLE_FILTER.LABEL.DATE',
              })}
              monthJumpButtons
              value={date}
              onChange={onDateChange}
              onClearButtonClick={handleClearButton}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2} xl={3}>
            {isUserAuthorized && (
              <ReactSelect
                name="users"
                label={intl.formatMessage({
                  id: 'WORK_TIME.SELECT_USER',
                })}
                options={timesheetData || []}
                defaultValue={
                  selectedUser?.name.length ? selectedUser : userData
                }
                handleChange={handleSelectUser}
              />
            )}
          </Grid>
        </Grid>
        <Grid container item md={12} lg={6}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            width="100%"
          >
            <ContainedButton
              onClick={setIsDateRangeDialogOpen}
              endIcon={<DateRange />}
              style={{ marginRight: '1rem' }}
            >
              {intl.formatMessage({
                id: 'WORK_TIME_TAB.REPORT_RANGE',
              })}
            </ContainedButton>
            {isAccountingOrSales && (
              <DownloadFileButton
                marginRight="1rem"
                url={downloadExcelUrl}
                buttonLabel="BUTTON.LABEL.DOWNLOAD_AS_XLSX"
                errorLabel="DOWNLOAD_XLSX.TOAST_DENIED"
                fallbackFileName={`timesheet_${username}.xlsx`}
              />
            )}
            <AssignButton
              id="assignButton"
              endIcon={<AddIcon />}
              disabled={!reportAvailable || isTimesheetDisabled}
              size="large"
              onClick={handleAddChecked}
            >
              {intl.formatMessage({
                id: 'WORK_TIME_TAB.ASSIGN_BUTTON',
              })}
            </AssignButton>
          </Box>
        </Grid>
      </GridWrapper>
      {corrections.dates.length + corrections.projects.length > 0 && (
        <Box mb={2}>
          <Alert severity="error">
            <AlertTitle>
              {intl.formatMessage(
                { id: 'WORK_TIME.NEEDS_CORRECTION.TITLE' },
                {
                  count: corrections.dates.length,
                  custom: corrections.projects.length,
                },
              )}
            </AlertTitle>
            {intl.formatMessage(
              { id: 'WORK_TIME.NEEDS_CORRECTION.MESSAGE' },
              {
                dates: [
                  ...corrections.dates.map((day) =>
                    moment(day).format('DD/MM'),
                  ),
                  ...corrections.projects,
                ].join(', '),
              },
            )}
          </Alert>
        </Box>
      )}
      <FullReportTableContainer component={Paper} $loading={loading}>
        <TableComponent size="small">
          <>
            <TableHeader>
              <TableRow $main>
                {fullReportHeadData.map((headData, index) => (
                  <TableHeadCell
                    align={headData.align}
                    width={headData.width}
                    key={`${index} - ${headData.key}`}
                    $action={headData.key === 'action'}
                  >
                    {intl.formatMessage({ id: headData.label })}
                  </TableHeadCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.length ? (
                sortedFields.map(({ entry, originalIndex: index }) => {
                  const isFreeDay = checkIfDayIsFree(entry.dayId, entry);
                  const isToday = moment().isSame(moment(entry.date), 'day');

                  const isInvalidWorktime = invalidWorktime
                    .map((day) => day.id)
                    .includes(Number(entry.id));

                  return (
                    <FullReportRow
                      key={`fullReportDay-${entry.dayId}`}
                      viewedUsername={timesheetOwner}
                      orderInScope={orderScopeByDate.get(
                        String(entry.date || '').slice(0, 10),
                      )}
                      hoursOnly={hoursOnly}
                      showOrder={showOrder}
                      showMultiplier={showMultiplier}
                      reportingMode={reportingMode}
                      exemptClientIds={exemptClientIds}
                      recordsDaysWithoutWork={daysWithoutWorkRecorded}
                      isFreeDay={isFreeDay}
                      isToday={isToday}
                      entry={entry}
                      index={index}
                      register={register}
                      rowRef={rowRef}
                      control={control}
                      isLeaveDay={isLeaveDay(entry.dayId)}
                      orderPending={entry.orderId === undefined}
                      clientOrProjectChanged={pendingOrderDayIds.has(
                        entry.dayId,
                      )}
                      savedReportsWork={savedDayReportsWork(entry.dayId)}
                      savedDay={getSavedDay(entry.dayId)}
                      clientsData={clientsData}
                      projectOptions={getProjectsOptions(entry.dayId)}
                      invalidWorktimeTitle={getInvalidWorktimeTitle(
                        intl,
                        invalidWorktime,
                        Number(entry.dayId),
                      )}
                      isInvalidWorktime={isInvalidWorktime}
                      onInvalidWorktime={onInvalidWorktime}
                      defaultProject={getDefaultProject(entry)}
                      checkWhatToRender={checkWhatToRender}
                      handleClientChange={handleClientChange}
                      handleProjectChange={handleProjectChange}
                      isTimesheetDisabled={isTimesheetDisabled}
                      deleteEntryFromDailytime={deleteEntryFromDailytime}
                      handleFileUploadClick={handleFileUploadClick}
                      addEntryToDailytime={addEntryToDailytime}
                      handleRefreshAfterTimeChange={
                        handleRefreshAfterTimeChange
                      }
                      setValue={setValue}
                    />
                  );
                })
              ) : (
                <EmptyRow />
              )}
            </TableBody>
            {fullReportData && myTimesheetLoaded && (
              <TableFooter>
                <TableFooterRow>
                  {isEmploymentContract && (
                    <>
                      <TableFooterCell>
                        {`${intl.formatMessage({
                          id: 'SUMMARY.MEDICAL_LEAVE',
                        })}: ${fullReportData.medicalLeaveDays}`}
                      </TableFooterCell>

                      <TableFooterCell>
                        {`${intl.formatMessage({
                          id: 'SUMMARY.VACATIONS',
                        })}: ${fullReportData.vacationDays}`}
                      </TableFooterCell>
                    </>
                  )}
                  {isEmploymentContract ? (
                    <TableFooterCell $whiteText>
                      {`${intl.formatMessage({
                        id: 'SUMMARY.DAYS_OFF',
                      })}: ${fullReportData.daysOff}`}
                    </TableFooterCell>
                  ) : (
                    <>
                      <ServiceBreakCounterCell days={fullReportData.days} />
                      <DaysWithoutWorkCounterCell days={fullReportData.days} />
                    </>
                  )}
                  <TableFooterCell>
                    {`${intl.formatMessage({
                      id: 'TABLE.HEAD.REPORTED_DAYS',
                    })}: ${fullReportData.reportedDays}`}
                  </TableFooterCell>
                  <TableFooterCell colSpan={footerSpans.notReported}>
                    {`${intl.formatMessage({
                      id: 'TABLE.HEAD.NOT_REPORTED_DAYS',
                    })}: ${fullReportData.unreportedDays}`}
                    {footerSpans.settledInline &&
                      ` · ${intl.formatMessage({
                        id: 'WORK_TIME_SETTLED_TEXT',
                      })}`}
                  </TableFooterCell>
                  {footerSpans.settled > 0 ? (
                    <TableFooterCell colSpan={footerSpans.settled}>
                      <ToolTipCell
                        id="settledTooltip"
                        styles={{
                          cellTooltipStyles: { justifyContent: 'flex-end' },
                          wrapperStyles: {
                            marginTop: '0',
                            marginBottom: '0',
                          },
                        }}
                        bigger
                        text={intl.formatMessage({
                          id: 'WORK_TIME_SETTLED_TEXT',
                        })}
                        tooltip={intl.formatMessage({
                          id: 'WORK_TIME_SETTLED_TOOLTIP',
                        })}
                      />
                    </TableFooterCell>
                  ) : null}
                  {footerSpans.filler > 0 && (
                    <TableFooterCell colSpan={footerSpans.filler} />
                  )}
                  <TableFooterCell $action />
                </TableFooterRow>
              </TableFooter>
            )}
          </>
        </TableComponent>
      </FullReportTableContainer>
      {fullReportData?.customDailyTimes.filter(
        (dailyTime) => dailyTime.reportingCustomData,
      ).length !== 0 && (
        <CustomProjectsContainer>
          <TableComponent size="small">
            <TableHeader>
              <TableRow $main>
                {customHeadData.map((headData, index) => (
                  <TableHeadCell
                    align={headData.align}
                    width={headData.width}
                    key={`${index} - ${headData.key}`}
                  >
                    {intl.formatMessage({ id: headData.label })}
                  </TableHeadCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {fullReportData?.customDailyTimes
                .filter((dailyTime) => dailyTime.reportingCustomData === true)
                .map((custom) => (
                  <TableRow key={custom.projectId}>
                    <TableCell>{custom.projectName}</TableCell>
                    <TableCellCustom>
                      {custom.project.multiplicator}
                    </TableCellCustom>
                    <TableCellCustom>{custom.workUnit}</TableCellCustom>
                    <TableCellCustom>
                      {`${custom.reportingCustomDataType[0].toUpperCase()}${custom.reportingCustomDataType
                        .substring(1)
                        .toLowerCase()}`}
                    </TableCellCustom>
                    {showOrder && (
                      <TableCellCustom>
                        <OrderCellContent
                          orderId={custom.orderId}
                          orderNumber={custom.orderNumber}
                          orderHours={custom.orderHours}
                          orderDays={custom.orderDays}
                          orderStatus={custom.orderStatus}
                          needsCorrection={custom.needsCorrection}
                          unresolvedIsPending={false}
                        />
                      </TableCellCustom>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </TableComponent>
        </CustomProjectsContainer>
      )}
      <FullReportSaveControls
        control={control}
        dirtyFields={formState.dirtyFields}
        hoursOnly={hoursOnly}
        getSavedDay={getSavedDay}
        isBusy={loading || loadingToDisableButton}
        onSubmit={onSubmit}
        setIsFormedChanged={setIsFormedChanged}
      />
      {isDateRangeDialogOpen && (
        <ProjectDateRangeDialog
          isDialogOpen={isDateRangeDialogOpen}
          clientsData={clientsData}
          projectsData={projectsData}
          date={date}
          onClose={setIsDateRangeDialogOpen}
          fetchReport={fetchFullReport}
        />
      )}
      {addChecked && (
        <AssignProjectDialog
          addChecked={addChecked}
          handleAddChanged={handleAddChecked}
          fetchReport={fetchFullReport}
          fetchClientsAndProjects={fetchClientsAndProjects}
        />
      )}
      {openFileUploadArea && (
        <FilesUploadDialog
          files={[]}
          previouslyUploadedFiles={uploadedDayFiles}
          addChecked={openFileUploadArea}
          handleAddChanged={handleFileDialogClose}
          sendFile={sendFile}
          deleteFile={deleteFile}
          setAreFilesSend={setAreFilesSend}
        />
      )}
    </>
  );
};
export default memo(FullReportTable);
